import * as React from 'react';

import type { IRecommendations } from '../../types/newbuildingRecommendations';
import { getPopularNewbuildingPlaceholderImageUrl } from './getPopularNewbuildingPlaceholderImageUrl';
import { NewbuildingLocation } from './NewbuildingLocation';
import * as styles from './RedesignedNewbuildingRecommendationCard.css';

type TRedesignedNewbuildingRecommendationCardProps = {
  recommendation: IRecommendations;
  onClick(): void;
};

function getNewbuildingTitle(name: string) {
  const trimmedName = name.trim();

  if (!trimmedName) {
    return 'ЖК';
  }

  return trimmedName.startsWith('ЖК') ? trimmedName : `ЖК ${trimmedName}`;
}

export const RedesignedNewbuildingRecommendationCard: React.FC<TRedesignedNewbuildingRecommendationCardProps> = ({
  recommendation,
  onClick,
}) => {
  const { id, media, minPrice, name, status, underground, url, address, builder } = recommendation;
  const title = getNewbuildingTitle(name);
  const primaryImageUrl = media[0]?.url;
  const placeholderImageUrl = getPopularNewbuildingPlaceholderImageUrl(id || name);
  const [imageUrl, setImageUrl] = React.useState(primaryImageUrl || placeholderImageUrl);
  const [showBuilderLogo, setShowBuilderLogo] = React.useState(Boolean(builder?.logoUrl));

  React.useEffect(() => {
    setImageUrl(primaryImageUrl || placeholderImageUrl);
  }, [primaryImageUrl, placeholderImageUrl]);

  React.useEffect(() => {
    setShowBuilderLogo(Boolean(builder?.logoUrl));
  }, [builder?.logoUrl]);

  const handleImageError = React.useCallback(() => {
    setImageUrl(currentImageUrl => (currentImageUrl === placeholderImageUrl ? undefined : placeholderImageUrl));
  }, [placeholderImageUrl]);

  const handleBuilderLogoError = React.useCallback(() => {
    setShowBuilderLogo(false);
  }, []);

  return (
    <a
      data-testid="RedesignedNewbuildingRecommendationCard"
      data-name="RedesignedNewbuildingRecommendationCard"
      href={url}
      target="_blank"
      className={styles['card']}
      onClick={onClick}
      rel="noreferrer"
    >
      <div className={styles['photo']}>
        {imageUrl && (
          <img
            className={styles['image']}
            src={imageUrl}
            alt={title}
            loading="lazy"
            decoding="async"
            onError={handleImageError}
          />
        )}
        {builder?.logoUrl && showBuilderLogo && (
          <span className={styles['builder-logo']}>
            <img
              className={styles['builder-logo-image']}
              src={builder.logoUrl}
              alt={builder.name ? `Логотип ${builder.name}` : ''}
              loading="lazy"
              decoding="async"
              onError={handleBuilderLogoError}
            />
          </span>
        )}
      </div>

      <div className={styles['content']}>
        <span className={styles['builder']}>{builder?.name ? `Застройщик ${builder.name}` : 'Застройщик'}</span>
        <span className={styles['title']}>{title}</span>
        {minPrice && <span className={styles['price']}>{minPrice}</span>}
        {status && <span className={styles['status']}>{status}</span>}
        <NewbuildingLocation underground={underground} address={underground ? null : address} />
      </div>
    </a>
  );
};
